import usePinValidationModal from '@/Components/Modal/usePinValidationModal';
import useSimpleModal from '@/Components/Modal/useSimpleModal';
import { LABELS_CONFIG } from '@/Hooks/PUJ/constants';
import useActivePackages from '@/Hooks/useActivePackages';
import useLinkAccount, { AccountType } from '@/Hooks/useLinkAccount';
import useUnsubscribedPackages from '@/Hooks/useUnsubscribedPackages';
import Preloader from '@/Layouts/containers/Preloader';
import getLabel from '@/Utils/LabelsConfig';
import navigateToPackage, { getDiscountPrice, getBasePrice } from '@/Utils/Puj';
import Icon from '@ant-design/icons';
import { ReactComponent as InfoIcon } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_info.svg';
import { Button } from 'antd';
import { isEmpty, isNil } from 'ramda';
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { hooks, selectors } from '@/Store';
import styles from './styles.module.scss';

export const ActivePackageItem = ({ packageItem }) => {
  return (
    <div className={styles.packageItem}>
      <img className={styles.logoImg} src={packageItem?.image?.uri} alt={packageItem?.title} />
      <div className={styles.details}>
        <span className={styles.title}>{packageItem?.title}</span>
      </div>
      <div className={styles.status}>Active</div>
    </div>
  );
};

export const AddPackageItem = ({ packageItem, handleAddClick }) => {
  const basePrice = packageItem?.price?.basePrice?.amount;
  const discount = packageItem?.price?.discount?.value;
  const offerId = packageItem?.id;

  const history = useHistory();
  const location = useLocation();
  const { openModal } = useSimpleModal();

  // eslint-disable-next-line prefer-const
  let [discountPriceInteger, discountPriceDecimal] = (basePrice - (discount || 0))
    .toString()
    .split('.');
  discountPriceDecimal = discountPriceDecimal ? discountPriceDecimal.padEnd(2, '0') : '00';

  const onDetailsBtnClick = () => {
    navigateToPackage({
      offerId,
      history,
      location,
      openModal,
    });
  };

  return (
    <div className={styles.packageItem}>
      <img
        className={styles.logoImg}
        src={packageItem?.package?.image?.uri}
        alt={packageItem?.title}
      />
      <div className={styles.details}>
        <span className={styles.title}>{packageItem?.title}</span>
        <div className={styles.offerDetails}>
          <span className={styles.actualPrice}>{discount && `$${basePrice}`}</span>
          <span className={styles.currency}>$</span>
          <span className={styles.discountPrice}>{discountPriceInteger}</span>
          <span className={styles.extraDetails}>
            <span className={styles.discountPriceDecimal}>.{discountPriceDecimal}</span>
            <span className={styles.billingFrequency}>/mth</span>
          </span>
        </div>
      </div>
      <Button type="primary" onClick={handleAddClick}>
        Add
      </Button>
      <Button className={styles.intoButton} type="primary" onClick={onDetailsBtnClick}>
        <Icon component={InfoIcon} />
      </Button>
    </div>
  );
};

const MyAccount: React.FC = () => {
  const { isAccountLinked } = useLinkAccount();
  const hasActiveAccount = isAccountLinked(AccountType.SkyDth);
  const isPurchasingAvailable = hooks.useSelector(selectors.customer?.isPurchasingAvailable);
  const { getActivePackages, result: activePackagesResult } = useActivePackages(hasActiveAccount);
  const { getUnsubscribedPackages, result: unSubPackagesResult } = useUnsubscribedPackages(
    hasActiveAccount,
  );
  useEffect(() => {
    if (hasActiveAccount && isPurchasingAvailable) {
      getActivePackages();
      getUnsubscribedPackages();
    }
    // this is to prevent multiple calls to the API also to avoid api calls for sky open users
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: activePackagesData, loading: isActivePkgsLoading } = activePackagesResult;
  const { data: unsubscribedPkgsData, loading: isUnSubPkgsLoading } = unSubPackagesResult;
  const activePackages = activePackagesData?.experience?.customer?.packages ?? [];
  const unsubscribedPkgs = unsubscribedPkgsData?.experience?.customer?.offers ?? [];

  const labelManagePackage = getLabel(LABELS_CONFIG.MANAGE_PACKAGE);
  const labelNeedActiveAccount = getLabel(LABELS_CONFIG.NEED_ACTIVE_ACCOUNT_TO_PURCHASE);
  const labelAlreadyInSubs = getLabel(LABELS_CONFIG.ALREADY_IN_SUBSCRIPTION);
  const labelCancelPkgHint = getLabel(LABELS_CONFIG.CANCEL_PKG_HINT);
  const labelAddPkgOnRollingPlan = getLabel(LABELS_CONFIG.ADD_PKG_ON_ROLLING_MONTHLY_PLAN);
  const labelNeedPinToAddPkg = getLabel(LABELS_CONFIG.NEED_PIN_TO_ADD_PKG);
  const labelRestartSkyAccount = getLabel(LABELS_CONFIG.RESTART_SKY_ACCOUNT);

  const { openPinModal } = usePinValidationModal();

  const onAddBtnClick = pkg => {
    const { price } = pkg;
    const discountedPrice = getDiscountPrice(
      price?.basePrice?.amount,
      price?.discount?.value,
      price?.billingFrequency,
    );
    const actualPrice = getBasePrice(price?.basePrice?.amount, price?.billingFrequency);
    openPinModal({
      offerId: pkg?.id,
      packageName: pkg?.title,
      packagePrice: discountedPrice ?? actualPrice,
      collectionId: pkg?.collectionLink?.collection.id,
    });
  };
  let subTitleText = labelManagePackage;
  if (!hasActiveAccount) {
    subTitleText = labelNeedActiveAccount;
  } else if (hasActiveAccount && !isPurchasingAvailable) {
    subTitleText = labelRestartSkyAccount;
  }
  return (
    <Preloader isLoading={isActivePkgsLoading || isUnSubPkgsLoading}>
      <div className={styles.pageTitle}>{subTitleText}</div>

      {!isNil(activePackages) && !isEmpty(activePackages) && (
        <>
          <div className={styles.sectionTitle}>{labelAlreadyInSubs}</div>
          <div className={styles.sectionSubTitle}>{labelCancelPkgHint}</div>
          <div className={styles.activePackagesList}>
            {activePackages.map(item => (
              <ActivePackageItem packageItem={item?.package} />
            ))}
          </div>
        </>
      )}
      {!isNil(unsubscribedPkgs) && !isEmpty(unsubscribedPkgs) && (
        <>
          <div className={styles.sectionTitle}>{labelAddPkgOnRollingPlan}</div>
          <div className={styles.sectionSubTitle}>{labelNeedPinToAddPkg}</div>
          <div className={styles.activePackagesList}>
            {unsubscribedPkgs.map(item => (
              <AddPackageItem
                packageItem={item}
                key={item?.offerId}
                handleAddClick={() => onAddBtnClick(item)}
              />
            ))}
          </div>
        </>
      )}
    </Preloader>
  );
};

export default MyAccount;
